import { useEffect, useMemo, useState } from "react";
import {
  Search,
  ChevronDown,
  Users,
  Phone,
  Mail,
  MapPin,
  Plus,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import { getTeamDirectory, addToTeamDirectory } from "../utils/firebasefunctions";
import PageHeader from "../components/PageHeader";

// Shared, team-wide directory of the professionals on deals (agents, lenders,
// appraisers, closers…). Backed by the shared Team Edelstein Google Contacts
// account; entries and their deal-address history (in Notes) are populated
// automatically when deals are saved — or added by hand here.
export default function Directory() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const blankPro = { name: "", role: "", company: "", phone: "", email: "" };
  const [form, setForm] = useState(blankPro);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const load = () => {
    return getTeamDirectory()
      .then((rows) => {
        if (!Array.isArray(rows)) setError(true);
        setContacts(rows || []);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const canSave =
    form.name.trim() && (form.phone.trim() || form.email.trim());

  const handleAdd = async () => {
    if (!canSave) {
      toast.warn("Enter a name plus a phone or an email");
      return;
    }
    setSaving(true);
    try {
      await addToTeamDirectory({
        name: form.name.trim(),
        role: form.role.trim(),
        company: form.company.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
      });
      toast.success("Added to the Team Directory");
      setAddOpen(false);
      setForm(blankPro);
      setSearch(form.name.trim()); // show them right away
      load();
    } catch (e) {
      toast.error("Couldn't save — try again");
    } finally {
      setSaving(false);
    }
  };

  const RESULT_CAP = 100; // don't render thousands of cards at once
  const q = search.trim().toLowerCase();

  const allMatches = useMemo(() => {
    if (!q) return [];
    return contacts
      .filter(
        (c) =>
          (c.name || "").toLowerCase().includes(q) ||
          (c.email || "").toLowerCase().includes(q) ||
          (c.phone || "").toLowerCase().includes(q),
      )
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [contacts, q]);

  const filtered = allMatches.slice(0, RESULT_CAP);

  // Notes hold the deal/address history, one entry per line.
  const historyLines = (notes) =>
    String(notes || "")
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);

  return (
    <>
    <PageHeader
      title="Team Directory"
      actions={
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          className="inline-flex items-center gap-1.5 bg-[#1E293B] text-white rounded-lg px-3 py-2 text-sm font-semibold"
        >
          <Plus size={14} /> Add Professional
        </button>
      }
    />
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <p className="text-sm text-gray-500 mb-5">
        Shared professionals from the team Google account, with their deal
        history. Populated automatically as deals are saved — or add someone
        yourself.
      </p>

      <div className="relative mb-5">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email, or phone…"
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-[#1E293B]"
        />
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-400 text-sm">Loading directory…</div>
      ) : error ? (
        <div className="py-12 text-center text-gray-400 text-sm">
          <Users size={28} className="mx-auto text-gray-300 mb-2" />
          Couldn't load the directory. Make sure the shared Team Edelstein Google
          account is connected.
        </div>
      ) : !q ? (
        <div className="py-12 text-center text-gray-400 text-sm">
          <Search size={28} className="mx-auto text-gray-300 mb-2" />
          Search {contacts.length.toLocaleString()} contacts by name, email, or
          phone to see their deal history.
        </div>
      ) : allMatches.length === 0 ? (
        <div className="py-12 text-center text-gray-400 text-sm">
          <Users size={28} className="mx-auto text-gray-300 mb-2" />
          No contacts match "{search.trim()}".
        </div>
      ) : (
        <div className="space-y-2">
          {allMatches.length > RESULT_CAP && (
            <p className="text-[11px] text-gray-400 px-1">
              Showing the first {RESULT_CAP} of {allMatches.length.toLocaleString()} matches — keep typing to narrow it down.
            </p>
          )}
          {filtered.map((c) => {
            const open = openId === c.resourceName;
            const history = historyLines(c.notes);
            return (
              <div key={c.resourceName} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setOpenId(open ? null : c.resourceName)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50/60 transition"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-slate-900 truncate">
                      {c.name || "(no name)"}
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-gray-500 mt-0.5 flex-wrap">
                      {c.email && (
                        <span className="inline-flex items-center gap-1">
                          <Mail size={11} /> {c.email}
                        </span>
                      )}
                      {c.phone && (
                        <span className="inline-flex items-center gap-1">
                          <Phone size={11} /> {c.phone}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="text-[11px] text-gray-400 flex-shrink-0">
                    {history.length} {history.length === 1 ? "deal" : "deals"}
                  </span>
                  <ChevronDown
                    size={16}
                    className={`text-gray-400 flex-shrink-0 transition-transform ${open ? "" : "-rotate-90"}`}
                  />
                </button>

                {open && (
                  <div className="border-t border-gray-100 px-4 py-3 bg-gray-50/40">
                    {history.length === 0 ? (
                      <p className="text-xs text-gray-400">No deal history yet.</p>
                    ) : (
                      <ul className="space-y-1.5">
                        {history.map((line, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
                            <MapPin size={12} className="text-gray-400 flex-shrink-0 mt-0.5" />
                            <span className="text-slate-800">{line}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>

    {addOpen && (
      <>
        <div
          className="fixed inset-0 bg-black/40 z-40"
          onClick={() => !saving && setAddOpen(false)}
        />
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md pointer-events-auto overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-900 text-white">
              <h3 className="text-base font-semibold">Add Professional</h3>
              <button
                onClick={() => !saving && setAddOpen(false)}
                className="p-1 text-slate-300 hover:text-white"
              >
                <X size={16} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Name *
                </label>
                <input
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#1E293B]"
                  value={form.name}
                  onChange={set("name")}
                  autoFocus
                  placeholder="Jane Doe"
                />
              </div>
              <div className="flex gap-3">
                <div className="w-1/2">
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    Role
                  </label>
                  <input
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#1E293B]"
                    value={form.role}
                    onChange={set("role")}
                    placeholder="Lender, Closer…"
                  />
                </div>
                <div className="w-1/2">
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    Company
                  </label>
                  <input
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#1E293B]"
                    value={form.company}
                    onChange={set("company")}
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-1/2">
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    Phone
                  </label>
                  <input
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#1E293B]"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="612-555-0123"
                  />
                </div>
                <div className="w-1/2">
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    Email
                  </label>
                  <input
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#1E293B]"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="name@example.com"
                  />
                </div>
              </div>
              <p className="text-[11px] text-slate-400">
                Needs a name plus a phone or an email. They'll be available
                anywhere the software asks for a professional.
              </p>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
              <button
                onClick={() => setAddOpen(false)}
                disabled={saving}
                className="px-4 py-2 text-sm font-semibold rounded-lg border border-slate-300 text-slate-700"
              >
                Cancel
              </button>
              <button
                onClick={handleAdd}
                disabled={saving || !canSave}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-[#1E293B] text-white disabled:opacity-50"
              >
                {saving ? "Saving…" : "Add to Directory"}
              </button>
            </div>
          </div>
        </div>
      </>
    )}
    </>
  );
}
